import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { AlertCircle, Info } from 'lucide-react';

/**
 * CategorySpecificAttributes - Renders dynamic fields based on subcategory selection
 * 
 * This component is used by the rental form to show different fields
 * based on the selected subcategory (e.g., Laptops, Hire Vehicle, Hotels)
 */
const CategorySpecificAttributes = ({
  schema,
  formData,
  errors,
  handleChange,
  subcategory, // The selected subcategory value
}) => {
  // Normalize subcategory name to schema key (e.g. "Hall / Venue Booking" → "hall_venue_booking")
  const normalizeToKey = (s) => s.toLowerCase()
    .replace(/[/&\s]+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

  // Get the subcategory config from the schema
  const subcategoryConfig = useMemo(() => {
    const cardConfig = schema?.cards?.categorySpecificAttributes;
    if (!cardConfig?.subcategoryConfigs || !subcategory) {
      return null;
    }
    // Exact match first
    if (cardConfig.subcategoryConfigs[subcategory]) {
      return cardConfig.subcategoryConfigs[subcategory];
    }
    // Normalized match (e.g. "Hall / Venue Booking" → "hall_venue_booking")
    const normalizedKey = normalizeToKey(subcategory);
    return cardConfig.subcategoryConfigs[normalizedKey] || null;
  }, [schema, subcategory]);

  // If no subcategory selected or no config for it, show placeholder
  if (!subcategory) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Item Details</CardTitle>
          <CardDescription>
            Select a rental type above to see specific fields
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 p-4 bg-gray-50 rounded-lg text-gray-500">
            <Info className="h-5 w-5" />
            <span>Please select a rental category and type first</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!subcategoryConfig) {
    return null; // No specific fields for this subcategory
  }

  const { title, fields } = subcategoryConfig;

  // Helper function to evaluate conditional visibility
  const evaluateCondition = (condition) => {
    if (condition === undefined || condition === null) return true;
    if (typeof condition === 'boolean') return condition;
    if (typeof condition === 'string') {
      // "!fieldName" negates the usual truthy check, e.g. "!is_hotel"
      if (condition.startsWith('!')) {
        return !formData[condition.slice(1)];
      }
      return !!formData[condition];
    }
    return true;
  };

  // Render a single field
  const renderField = (field) => {
    const {
      name,
      label,
      type,
      placeholder,
      helperText,
      required,
      options,
      showIf,
      rows,
    } = field;

    // Check conditional visibility
    if (showIf && !evaluateCondition(showIf)) {
      return null;
    }

    const value = formData[name] ?? '';
    const error = errors?.[name];
    const errorBorderClass = error ? 'border-red-500 ring-1 ring-red-500' : '';

    // Render label inline instead of as nested component
    const labelElement = (
      <Label htmlFor={name} className={`text-sm font-medium ${error ? 'text-red-600' : ''}`}>
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </Label>
    );

    switch (type) {
      case 'text':
      case 'number':
      case 'time':
        return (
          <div key={name} className="space-y-2">
            {labelElement}
            <Input
              id={name}
              type={type}
              value={value}
              onChange={(e) => handleChange(name, type === 'number' ? parseFloat(e.target.value) || '' : e.target.value)}
              placeholder={placeholder}
              className={errorBorderClass}
              data-testid={`field-${name}`}
            />
            {helperText && <p className="text-xs text-gray-500">{helperText}</p>}
            {error && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />{error}
              </p>
            )}
          </div>
        );

      case 'textarea':
        return (
          <div key={name} className="space-y-2">
            {labelElement}
            <Textarea
              id={name}
              value={value}
              onChange={(e) => handleChange(name, e.target.value)}
              placeholder={placeholder}
              rows={rows || 3}
              className={errorBorderClass}
              data-testid={`field-${name}`}
            />
            {helperText && <p className="text-xs text-gray-500">{helperText}</p>}
            {error && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />{error}
              </p>
            )}
          </div>
        );

      case 'select':
        const selectOptions = Array.isArray(options) ? options : [];
        return (
          <div key={name} className="space-y-2">
            {labelElement}
            <Select
              value={value?.toString()}
              onValueChange={(val) => handleChange(name, val)}
            >
              <SelectTrigger className={errorBorderClass} data-testid={`field-${name}`}>
                <SelectValue placeholder={placeholder || 'Select...'} />
              </SelectTrigger>
              <SelectContent>
                {selectOptions.map((opt) => (
                  <SelectItem
                    key={typeof opt === 'object' ? opt.value : opt}
                    value={(typeof opt === 'object' ? opt.value : opt)?.toString()}
                  >
                    {typeof opt === 'object' ? opt.label : opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {helperText && <p className="text-xs text-gray-500">{helperText}</p>}
            {error && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />{error}
              </p>
            )}
          </div>
        );

      case 'checkbox':
        return (
          <div key={name} className="flex items-center space-x-2 py-2">
            <Checkbox
              id={name}
              checked={!!value}
              onCheckedChange={(checked) => handleChange(name, checked)}
              data-testid={`field-${name}`}
            />
            <Label htmlFor={name} className="cursor-pointer text-sm">{label}</Label>
            {helperText && (
              <span className="text-xs text-gray-500 ml-2">({helperText})</span>
            )}
          </div>
        );

      case 'badge-select':
        const badgeOptions = Array.isArray(options) ? options : [];
        const selectedBadges = Array.isArray(value) ? value : [];
        return (
          <div key={name} className="space-y-2">
            {labelElement}
            <div className="flex flex-wrap gap-2">
              {badgeOptions.map((opt) => (
                <Badge
                  key={opt}
                  variant={selectedBadges.includes(opt) ? 'default' : 'outline'}
                  className="cursor-pointer transition-all"
                  onClick={() => {
                    const newValues = selectedBadges.includes(opt)
                      ? selectedBadges.filter((v) => v !== opt)
                      : [...selectedBadges, opt];
                    handleChange(name, newValues);
                  }}
                  data-testid={`badge-${name}-${opt}`}
                >
                  {opt}
                </Badge>
              ))}
            </div>
            {helperText && <p className="text-xs text-gray-500">{helperText}</p>}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Card data-testid="category-specific-attributes">
      <CardHeader>
        <CardTitle className="text-lg">{title || 'Item Details'}</CardTitle>
        <CardDescription>
          Fill in the specific details for your rental item
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fields.map((field) => renderField(field))}
        </div>
      </CardContent>
    </Card>
  );
};

export default CategorySpecificAttributes;
